/**
 * Ticket prices of the Events POC. The API stores them as an integer number of
 * cents (`priceCents`, null = free); the organizer types euros.
 */

/** Same bounds as the API (keeptouch-api src/events): 0,50 € – 10 000 €. */
export const EVENT_PRICE_MIN_CENTS = 50
export const EVENT_PRICE_MAX_CENTS = 1_000_000

const LOCALES: Record<string, string> = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' }

/** "25,00 €" (fr) / "€25.00" (en). */
export function formatEventPrice(cents: number, language = 'fr', currency = 'EUR'): string {
  return new Intl.NumberFormat(LOCALES[language] ?? 'fr-FR', {
    style: 'currency',
    currency,
  }).format(cents / 100)
}

/** "Gratuit" is a translation, so the caller passes the label for a free event. */
export function eventPriceLabel(
  event: { priceCents: number | null; payableCents?: number | null; currency?: string },
  freeLabel: string,
  language = 'fr',
): string {
  return event.priceCents === null
    ? freeLabel
    : formatEventPrice(event.payableCents ?? event.priceCents, language, event.currency)
}

/**
 * What the organizer typed ("12", "12,5", "12.50", "12,50 €", "1 200") → cents.
 * null when it is not a price with at most two decimals.
 */
export function parsePriceInput(text: string): number | null {
  const cleaned = text
    .replace(/[€\s\u00a0]/g, '')
    .replace(',', '.')
    .trim()
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  return Math.round(Number(cleaned) * 100)
}

/** Basis points → percent text: 500 → "5", 250 → "2,5" (comma or dot per language). */
export function formatBps(bps: number, language = 'fr'): string {
  return new Intl.NumberFormat(LOCALES[language] ?? 'fr-FR', { maximumFractionDigits: 2 }).format(
    bps / 100,
  )
}

/** Cents → what the price field shows when editing ("12,50", "12"). */
export function priceToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return ''
  const euros = cents / 100
  return Number.isInteger(euros) ? String(euros) : euros.toFixed(2).replace('.', ',')
}

/** 899 000 ms → "14:59". Never negative. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}
